import type { ComposerCommand } from '@/lib/desktop-shell/composer-commands';
import { filterComposerCommands } from '@/lib/desktop-shell/composer-commands';
import { filterSkills, type ModelChoice } from '@/lib/desktop-shell/composer-menu';
import type { RepositoryFile } from '@/lib/desktop-shell/repository-files';
import type { AvailablePluginSkill } from '@/lib/native-agent/types';
import { type MentionTarget, mentionArtifact } from '@/lib/side-panel/mention-targets';

/**
 * The composer's one picker (+, / and @): what it lists for a scope and a query, as sections of
 * rows in the order they show; ↑ ↓ walk them in that order. Pure: the component renders it.
 */

/** Unscoped (+), skills and commands (/ and $), or references (@). */
export type PickerScope = 'all' | 'skills' | 'mention';

/** A participant of a group conversation that @ notifies (or Everyone). */
export type PickerParticipant = { id: string; name: string; agentId: string; avatar?: unknown };

export type PickerAction = 'files' | 'screenshot' | 'mention-file' | 'camera' | 'photos';

/** A setting the + reaches (plan mode; on a phone permissions and where it runs too). */
export type PickerSetting = 'plan' | 'permissions' | 'environment' | 'model' | 'mcp';

export type PickerItem =
  | { key: string; type: 'action'; action: PickerAction; label: string }
  | { key: string; type: 'command'; command: ComposerCommand; label: string }
  | { key: string; type: 'skill'; skill: AvailablePluginSkill; label: string }
  | { key: string; type: 'file'; file: RepositoryFile; label: string }
  | { key: string; type: 'mention'; target: MentionTarget; label: string }
  | { key: string; type: 'participant'; participant: PickerParticipant; label: string }
  | { key: string; type: 'model-choice'; choice: ModelChoice; label: string }
  | { key: string; type: 'setting'; setting: PickerSetting; label: string };

type PickerSectionId =
  | 'actions'
  | 'commands'
  | 'recent-skills'
  | 'skills'
  | 'participants'
  | 'file'
  | 'persona'
  | 'chat'
  | 'artifact'
  | 'tab'
  | 'app'
  | 'terminal'
  | 'settings';

export type PickerSection = { id: PickerSectionId; label: string | null; items: PickerItem[] };

/**
 * The reference kinds of @, in the order their pills and sections show (D7c): what the
 * conversation works on first (its files, artifacts, open tabs), then whom and what else it can
 * bring in.
 */
const MENTION_SECTIONS = ['file', 'artifact', 'tab', 'chat', 'persona', 'app', 'terminal'] as const;
type MentionSection = (typeof MENTION_SECTIONS)[number];

const SECTION_LABEL: Record<PickerSectionId, string> = {
  actions: 'Add',
  commands: 'Commands',
  'recent-skills': 'Recent skills',
  skills: 'Skills',
  participants: 'Notify participants',
  file: 'Files',
  persona: 'Personas',
  chat: 'Conversations',
  artifact: 'Artifacts',
  tab: 'Tabs',
  app: 'Apps',
  terminal: 'Terminals',
  settings: 'Settings',
};

/** A filter pill of @: everything, one kind, or one type of artifact. */
export type MentionFilter = {
  id: string;
  label: string;
  kind?: MentionSection;
  artifactType?: string;
};

export type PickerSources = {
  /** Crowdex's commands ("/" only). */
  commands?: readonly ComposerCommand[];
  skills: readonly AvailablePluginSkill[];
  /** The skills inserted last on this device, most recent first (their `uri`). */
  recentSkillIds?: readonly string[];
  targets: readonly MentionTarget[];
  /** The repository's files matching the query (already filtered). */
  files?: readonly RepositoryFile[];
  /** A group conversation's participants (its @ notifies them). */
  participants?: readonly PickerParticipant[];
  modelChoices?: readonly ModelChoice[];
  modelName?: string | null;
  /** Rows of the + without a query: which actions and settings it offers. */
  actions?: readonly PickerAction[];
  settings?: readonly PickerSetting[];
  /** Whether MCP servers can be reached (the MCP row found by typing). */
  mcp?: boolean;
};

/** How many recent skills the + shows. */
const RECENT_SKILL_COUNT = 3;
/** How many rows a section of an unscoped search shows. */
const SEARCH_SECTION_LIMIT = 6;

const ACTION_LABEL: Record<PickerAction, string> = {
  files: 'Add photos & files',
  screenshot: 'Take a screenshot',
  'mention-file': 'Mention a file',
  camera: 'Camera',
  photos: 'Photos',
};
/** What an action is also found by. */
const ACTION_WORDS: Record<PickerAction, string> = {
  files: 'add photos files attach upload images',
  screenshot: 'take a screenshot capture screen',
  'mention-file': 'mention a file repository',
  camera: 'camera photo',
  photos: 'photos images library',
};
const SETTING_LABEL: Record<PickerSetting, string> = {
  plan: 'Plan mode',
  permissions: 'Permissions',
  environment: 'Runs in',
  model: 'Model',
  mcp: 'MCP',
};
const SETTING_WORDS: Record<PickerSetting, string> = {
  plan: 'plan mode',
  permissions: 'permissions access',
  environment: 'runs in environment where',
  model: 'model',
  mcp: 'mcp plugins servers',
};

function includes(value: string, query: string): boolean {
  return value.toLowerCase().includes(query);
}

function skillLabel(skill: AvailablePluginSkill): string {
  return skill.displayName ?? skill.name;
}

function skillItem(skill: AvailablePluginSkill, prefix: string): PickerItem {
  return {
    key: `${prefix}:${skill.pluginName}:${skill.path}`,
    type: 'skill',
    skill,
    label: skillLabel(skill),
  };
}

function targetMatches(target: MentionTarget, query: string): boolean {
  return query.length === 0 || includes(`${target.label} ${target.detail} ${target.kind}`, query);
}

function uniqueTargets(targets: readonly MentionTarget[]): MentionTarget[] {
  return [...new Map(targets.map((target) => [`${target.kind}:${target.id}`, target])).values()];
}

function mentionItem(target: MentionTarget): PickerItem {
  return { key: `${target.kind}:${target.id}`, type: 'mention', target, label: target.label };
}

function fileItem(file: RepositoryFile): PickerItem {
  return { key: `file:${file.path}`, type: 'file', file, label: file.name };
}

/** The recent skills: those inserted last, else the first ones. */
export function recentSkills(
  skills: readonly AvailablePluginSkill[],
  recentIds: readonly string[],
  count = RECENT_SKILL_COUNT,
): AvailablePluginSkill[] {
  const byId = new Map(skills.map((skill) => [skill.uri, skill]));
  const recent = recentIds.flatMap((id) => {
    const skill = byId.get(id);
    return skill === undefined ? [] : [skill];
  });
  return (recent.length > 0 ? recent : skills).slice(0, count);
}

/** The artifact type of a reference, when it names an artifact. */
function artifactTypeOf(target: MentionTarget): string | null {
  return target.kind === 'artifact' ? (mentionArtifact(target)?.typeTitle ?? null) : null;
}

/**
 * The filter pills of @: All, then each kind present (Artifacts with its types once it is the
 * one chosen and there are several).
 */
export function mentionFilters(
  targets: readonly MentionTarget[],
  files: readonly RepositoryFile[],
  activeId: string,
): MentionFilter[] {
  const has = (kind: MentionSection) =>
    kind === 'file' ? files.length > 0 : targets.some((target) => target.kind === kind);
  const types = [...new Set(targets.flatMap((target) => artifactTypeOf(target) ?? []))].sort();
  const filters: MentionFilter[] = [{ id: 'all', label: 'All' }];
  for (const kind of MENTION_SECTIONS) {
    if (!has(kind)) continue;
    filters.push({ id: kind, label: SECTION_LABEL[kind], kind });
    if (kind === 'artifact' && activeId.startsWith('artifact') && types.length > 1)
      for (const type of types)
        filters.push({ id: `artifact:${type}`, label: type, kind, artifactType: type });
  }
  return filters;
}

function inFilter(filter: MentionFilter | undefined, kind: MentionSection, target?: MentionTarget) {
  if (filter?.kind === undefined) return true;
  if (filter.kind !== kind) return false;
  return (
    filter.artifactType === undefined ||
    (target !== undefined && artifactTypeOf(target) === filter.artifactType)
  );
}

function mentionSections(
  sources: PickerSources,
  query: string,
  filter: MentionFilter | undefined,
  limit: number | undefined,
): PickerSection[] {
  const targets = uniqueTargets(sources.targets).filter((target) => targetMatches(target, query));
  return MENTION_SECTIONS.flatMap((kind): PickerSection[] => {
    const items =
      kind === 'file'
        ? inFilter(filter, 'file')
          ? (sources.files ?? []).map(fileItem)
          : []
        : targets
            .filter((target) => target.kind === kind && inFilter(filter, kind, target))
            .map(mentionItem);
    const shown = limit === undefined ? items : items.slice(0, limit);
    return shown.length === 0 ? [] : [{ id: kind, label: SECTION_LABEL[kind], items: shown }];
  });
}

/** Rows found by typing a setting's words (and a model's name, which picks it at once). */
function settingSections(
  sources: PickerSources,
  query: string,
  skillsScope: boolean,
): PickerSection[] {
  if (query.length === 0) return [];
  const choices = (sources.modelChoices ?? []).filter(
    (choice) => includes(choice.name, query) || includes(choice.value, query),
  );
  const items: PickerItem[] = choices.map((choice) => ({
    key: `model:${choice.configId}:${choice.value}`,
    type: 'model-choice',
    choice,
    label: choice.name,
  }));
  const settings: PickerSetting[] = [
    ...(skillsScope ? [] : (sources.settings ?? [])),
    ...((sources.modelChoices ?? []).length > 0 && choices.length === 0 ? ['model' as const] : []),
    ...(sources.mcp ? ['mcp' as const] : []),
  ];
  for (const setting of settings) {
    const words =
      setting === 'model'
        ? `${SETTING_WORDS.model} ${sources.modelName ?? ''}`
        : SETTING_WORDS[setting];
    if (includes(words, query))
      items.push({
        key: `setting:${setting}`,
        type: 'setting',
        setting,
        label: SETTING_LABEL[setting],
      });
  }
  if (skillsScope && includes(ACTION_WORDS.files, query))
    items.push({ key: 'action:files', type: 'action', action: 'files', label: ACTION_LABEL.files });
  return items.length === 0 ? [] : [{ id: 'settings', label: SECTION_LABEL.settings, items }];
}

/**
 * The sections the picker shows:
 * - `all` without a query (+): the actions, the recent skills, the settings;
 * - `all` with one: everything that matches (actions, commands, skills, references, settings);
 * - `skills` (/ and $): the commands then the skills, and a typed model or setting;
 * - `mention` (@): the participants of a group, then the references by kind, under `filter`.
 */
export function pickerSections(input: {
  scope: PickerScope;
  query: string;
  sources: PickerSources;
  filter?: MentionFilter | undefined;
}): PickerSection[] {
  const query = input.query.trim().toLowerCase();
  const { sources } = input;
  const sections: PickerSection[] = [];
  const push = (
    id: PickerSectionId,
    items: PickerItem[],
    label: string | null = SECTION_LABEL[id],
  ) => {
    if (items.length > 0) sections.push({ id, label, items });
  };

  if (input.scope === 'mention') {
    if (sources.participants !== undefined && input.filter?.kind === undefined)
      push(
        'participants',
        sources.participants
          .filter((participant) => query.length === 0 || includes(participant.name, query))
          .map((participant) => ({
            key: `participant:${participant.id}`,
            type: 'participant',
            participant,
            label: participant.name,
          })),
      );
    sections.push(...mentionSections(sources, query, input.filter, undefined));
    return sections;
  }

  if (input.scope === 'skills') {
    push(
      'commands',
      filterComposerCommands(sources.commands ?? [], query).map((command) => ({
        key: `command:${command.id}`,
        type: 'command',
        command,
        label: command.name,
      })),
    );
    push(
      'skills',
      filterSkills(sources.skills, query).map((skill) => skillItem(skill, 'skill')),
    );
    sections.push(...settingSections(sources, query, true));
    return sections;
  }

  const actions = (sources.actions ?? []).filter(
    (action) => query.length === 0 || includes(ACTION_WORDS[action], query),
  );
  push(
    'actions',
    actions.map((action) => ({
      key: `action:${action}`,
      type: 'action',
      action,
      label: ACTION_LABEL[action],
    })),
    query.length === 0 ? null : SECTION_LABEL.actions,
  );

  if (query.length === 0) {
    push(
      'recent-skills',
      recentSkills(sources.skills, sources.recentSkillIds ?? []).map((skill) =>
        skillItem(skill, 'recent'),
      ),
    );
    push(
      'settings',
      (sources.settings ?? []).map((setting) => ({
        key: `setting:${setting}`,
        type: 'setting',
        setting,
        label: SETTING_LABEL[setting],
      })),
      null,
    );
    return sections;
  }

  push(
    'commands',
    filterComposerCommands(sources.commands ?? [], query)
      .slice(0, SEARCH_SECTION_LIMIT)
      .map((command) => ({
        key: `command:${command.id}`,
        type: 'command',
        command,
        label: command.name,
      })),
  );
  push(
    'skills',
    filterSkills(sources.skills, query)
      .slice(0, SEARCH_SECTION_LIMIT)
      .map((skill) => skillItem(skill, 'skill')),
  );
  sections.push(...mentionSections(sources, query, undefined, SEARCH_SECTION_LIMIT));
  sections.push(...settingSections(sources, query, false));
  return sections;
}

/** The rows in the order ↑ ↓ walk them. */
export function pickerItems(sections: readonly PickerSection[]): PickerItem[] {
  return sections.flatMap((section) => section.items);
}

/** Splits `text` around the first match of `query`, to show the match in bold. */
export function highlightParts(
  text: string,
  query: string,
): { before: string; match: string; after: string } | null {
  const needle = query.trim().toLowerCase();
  if (needle.length === 0) return null;
  const at = text.toLowerCase().indexOf(needle);
  if (at < 0) return null;
  return {
    before: text.slice(0, at),
    match: text.slice(at, at + needle.length),
    after: text.slice(at + needle.length),
  };
}
